import { noticeBrandWatermarkLayout } from './notice-brand-watermark-layout';
import {
  sameAssetReference,
  sameBrandReference,
  type DocumentAssetCatalog,
  type DocumentBrandVersion,
} from '../document-assets/document-assets.models';
import { CommonModule } from '@angular/common';
import {
  Component,
  AfterViewInit,
  EventEmitter,
  ElementRef,
  ViewChild,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren,
  inject,
  HostListener,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import {
  NoticeFontAccessError,
  NoticeFontLoaderService,
} from './notice-font-loader.service';
import { NoticeInlineEditorComponent } from './notice-inline-editor.component';
import { NoticeTablePropertiesComponent } from './notice-table-properties.component';
import { NoticeDocumentSettingsComponent } from './notice-document-settings.component';
import { NoticeReferencePropertiesComponent } from './notice-reference-properties.component';
import { NoticeInsertionPanelComponent } from './notice-insertion-panel.component';
import type { NoticeDiagnosticTarget } from './notice-diagnostic-navigation';
import {
  applyNoticeReferenceCommand,
  noticeReferenceSelections,
  type NoticeReferenceCommand,
  type NoticeReferenceSelection,
} from './notice-reference-commands';
import {
  applyNoticeDocumentSetting,
  noticeDocumentLogoChoices,
  noticeDocumentWatermarkChoices,
  type NoticeDocumentSetting,
} from './notice-document-settings';
import {
  applyNoticeStructureCommand,
  type NoticeStructureCommand,
} from './notice-structure-commands';
import type {
  NoticeBlock,
  NoticeCatalog,
  NoticeComposition,
  NoticeRun,
  NoticeProof,
  NoticeStyle,
} from './notice-fidelity.models';

export interface NoticePageChange {
  composition: NoticeComposition;
  selectedBlockId: string | null;
  groupKey?: string;
  /** The source owner acknowledges synchronous edits; this is never persisted. */
  accepted?: boolean;
}

/** Session-only presentation state. Never contains source text or proof data. */
export interface NoticePageViewState {
  zoom: number;
  outlineOpen: boolean;
  propertiesOpen: boolean;
  inspectorMode: 'element' | 'document' | 'reference';
  reference?: { blockId: string; referenceIndex: number };
  canvasScrollTop: number;
  canvasScrollLeft: number;
  propertiesScrollTop: number;
  outlineScrollTop: number;
  workspaceScrollTop: number;
}

export interface NoticePageViewStateChange {
  identity: number;
  state: NoticePageViewState;
}

interface PendingIssueFocus {
  target: NoticeDiagnosticTarget;
  source: string;
  viewIdentity: number;
  scopeIdentity: string;
  selectedBlockId: string | null;
  activeElement: Element | null;
  generation: number;
}

/** Editing projection of the saved composition. Final pagination belongs to the real PDF preview. */
@Component({
  selector: 'phms-notice-page-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UiButtonDirective,
    UiControlDirective,
    NoticeInlineEditorComponent,
    NoticeTablePropertiesComponent,
    NoticeDocumentSettingsComponent,
    NoticeReferencePropertiesComponent,
    NoticeInsertionPanelComponent,
  ],
  templateUrl: './notice-page-editor.component.html',
  styleUrl: './notice-page-editor.component.scss',
})
export class NoticePageEditorComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  private readonly fonts = inject(NoticeFontLoaderService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  @Input({ required: true }) composition!: NoticeComposition;
  @Input({ required: true }) catalog!: NoticeCatalog;
  @Input() selectedBlockId: string | null = null;
  /** Explicit correction navigation; ordinary selection never moves keyboard focus. */
  @Input() focusBlockOnOpen: string | null = null;
  @Input() focusIssueOnOpen: NoticeDiagnosticTarget | null = null;
  @Input() scopeIdentity = '';
  /** The source owner changes this identity when the document is replaced. */
  @Input() viewIdentity = 0;
  @Input() viewState: NoticePageViewState | null = null;
  @Input() disabled = false;
  @Input() canUndo = false;
  @Input() canRedo = false;
  @Input() proof: NoticeProof | null = null;
  @Input() assetCatalog: DocumentAssetCatalog | null = null;
  @Input() assetsLoading = false;
  @Input() assetsError = '';
  @Input() brandProfile: DocumentBrandVersion | null = null;
  @Input() brandLogoUrl = '';
  @Input() brandError = '';
  @Output() readonly loadAssets = new EventEmitter<void>();
  @Output() readonly documentChange = new EventEmitter<NoticePageChange>();
  @Output() readonly selectedBlockIdChange = new EventEmitter<string>();
  @Output() readonly historyCommand = new EventEmitter<'undo' | 'redo'>();
  @Output() readonly accessLost = new EventEmitter<void>();
  @Output() readonly breakHistoryGroup = new EventEmitter<void>();
  @Output() readonly fieldsRequested = new EventEmitter<void>();
  @Output() readonly issueFocusUnavailable = new EventEmitter<void>();
  @Output() readonly viewStateChange =
    new EventEmitter<NoticePageViewStateChange>();
  @ViewChildren(NoticeInlineEditorComponent)
  private editors!: QueryList<NoticeInlineEditorComponent>;
  @ViewChild('canvas') private canvas?: ElementRef<HTMLElement>;
  @ViewChild('workspace') private workspace?: ElementRef<HTMLElement>;
  @ViewChild('pageLayout') private pageLayout?: ElementRef<HTMLElement>;
  @ViewChild('pageToolbar') private pageToolbar?: ElementRef<HTMLElement>;
  @ViewChild('propertiesPane') private propertiesPane?: ElementRef<HTMLElement>;
  @ViewChild('outlinePane') private outlinePane?: ElementRef<HTMLElement>;
  @ViewChild('tokenTrigger')
  private tokenTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('blockTrigger')
  private blockTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild(NoticeInsertionPanelComponent)
  private insertionPanel?: NoticeInsertionPanelComponent;
  @ViewChild('insertionPanel', { read: ElementRef })
  private insertionPanelHost?: ElementRef<HTMLElement>;
  @ViewChild('pageSetupTrigger')
  private pageSetupTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('propertiesTrigger')
  private propertiesTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild(NoticeDocumentSettingsComponent)
  private documentSettings?: NoticeDocumentSettingsComponent;
  readonly fontReady = signal(false);
  readonly fontError = signal('');
  readonly insertError = signal('');
  zoom = 0.75;
  propertiesOpen = true;
  inspectorMode: 'element' | 'document' | 'reference' = 'element';
  referenceSelection: NoticeReferenceSelection | null = null;
  referenceError = '';
  settingsError = '';
  outlineOpen = false;
  blockPickerOpen = false;
  insertionKind: NoticeBlock['kind'] = 'paragraph';
  insertionPosition: 'before' | 'after' = 'after';
  structureMessage = '';
  structureError = false;
  tokenPickerOpen = false;
  tokenKind: 'field' | 'term' = 'field';
  tokenCode = '';
  tokenFormat = '';
  tokenSearch = '';
  private pendingTokenInsertion: object | null = null;
  private fontIdentity = '';
  private fontGeneration = 0;
  private destroyed = false;
  private pendingOpenFocus: string | null = null;
  private pendingIssueFocus: PendingIssueFocus | null = null;
  private scheduledIssueFocus: PendingIssueFocus | null = null;
  private pendingViewRestore: NoticePageViewState | null = null;
  private lastKnownViewState: NoticePageViewState | null = null;
  private suspendedViewState: NoticePageViewState | null = null;
  private viewRestoreScheduled = false;
  private viewGeneration = 0;
  private panelFocusGeneration = 0;
  private settingsReturnTarget: HTMLElement | null = null;
  // Same trusted synthetic seal as the pinned renderer; no remote asset loading.
  readonly seal =
    'data:image/svg+xml,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><circle cx="40" cy="40" r="36" fill="#FFFFFF" stroke="#183B56" stroke-width="4"/><circle cx="40" cy="40" r="27" fill="#E8EFF4" stroke="#183B56" stroke-width="2"/><path d="M22 43 L34 55 L59 28" fill="none" stroke="#183B56" stroke-width="6"/></svg>',
    );

  get contextEnabled(): boolean {
    return this.composition.contractCode === 'notice-document-context-proof';
  }
  get fields(): readonly { code: string; label: string }[] {
    return this.contextEnabled
      ? (this.catalog.documentContext?.fields ?? [])
      : this.catalog.fields;
  }
  get terms(): readonly { code: string; label: string }[] {
    return this.contextEnabled
      ? (this.catalog.documentContext?.terms ?? [])
      : this.catalog.terms;
  }
  get selected(): NoticeBlock | undefined {
    return (
      this.composition.blocks.find((b) => b.id === this.selectedBlockId) ??
      this.composition.blocks.find((b) => !!b.runs)
    );
  }
  get editable(): boolean {
    return (
      !this.disabled &&
      this.fontReady() &&
      (!this.composition.branding.profile ||
        (!!this.effectiveBrand &&
          !this.brandError &&
          (!this.effectiveBrand.style.logo || !!this.brandLogoUrl)))
    );
  }
  get tokenOptions(): readonly { code: string; label: string }[] {
    return this.tokenKind === 'field' ? this.fields : this.terms;
  }
  get filteredTokenOptions(): readonly { code: string; label: string }[] {
    const query = this.tokenSearch.trim().toLocaleLowerCase();
    return this.tokenOptions.filter(
      (token) =>
        !query ||
        token.label.toLocaleLowerCase().includes(query) ||
        token.code.toLocaleLowerCase().includes(query),
    );
  }
  get insertingToken(): boolean {
    return this.pendingTokenInsertion !== null;
  }
  get insertionMode(): 'token' | 'block' | null {
    return this.tokenPickerOpen
      ? 'token'
      : this.blockPickerOpen
        ? 'block'
        : null;
  }
  get insertionPanelBounds(): { top: number; height: number } | null {
    const workspace = this.workspace?.nativeElement;
    const layout = this.pageLayout?.nativeElement;
    if (!workspace || !layout) return null;
    const window = workspace.ownerDocument.defaultView;
    const rootSize =
      Number.parseFloat(
        window?.getComputedStyle(workspace.ownerDocument.documentElement)
          .fontSize ?? '',
      ) || 16;
    const inset = 0.35 * rootSize;
    const workspaceBounds = workspace.getBoundingClientRect();
    const layoutBounds = layout.getBoundingClientRect();
    // The narrow layout can scroll beneath its sticky toolbar and includes a
    // stacked inspector. Keep insertion inside the visible editing workspace.
    const top = Math.max(
      layoutBounds.top,
      workspaceBounds.top,
      this.pageToolbar?.nativeElement.getBoundingClientRect().bottom ??
        layoutBounds.top,
    );
    return {
      top: Math.max(0, top - layoutBounds.top) + inset,
      height: Math.max(
        0,
        Math.floor(
          Math.min(workspaceBounds.bottom, window?.innerHeight ?? Infinity) -
            top -
            2 * inset,
        ),
      ),
    };
  }
  get insertionPanelHeight(): number | null {
    return this.insertionPanelBounds?.height ?? null;
  }
  @HostListener('window:resize') insertionViewportResized(): void {
    if (this.insertionMode) this.rememberViewState();
  }
  get tokenFormats(): readonly string[] {
    return this.tokenKind === 'field' && this.contextEnabled
      ? (this.catalog.documentContext?.fields.find(
          (f) => f.code === this.tokenCode,
        )?.formats ?? [])
      : [];
  }
  get paperWidthPt(): number {
    return this.composition.page.size === 'a4' ? 595 : 612;
  }
  get paperHeightPt(): number {
    return this.composition.page.size === 'a4' ? 842 : 792;
  }
  get paperStyle(): Record<string, string | number> {
    return {
      width: `${this.paperWidthPt}pt`,
      minHeight: `${this.paperHeightPt}pt`,
      padding: `${this.composition.page.marginPt}pt`,
      fontSize: `${this.composition.page.fontSizePt}pt`,
      lineHeight: this.composition.page.lineHeight,
      zoom: this.zoom,
    };
  }
  get effectiveBrand(): DocumentBrandVersion | null {
    return sameBrandReference(
      this.brandProfile?.reference,
      this.composition.branding.profile,
    )
      ? this.brandProfile
      : null;
  }
  get accentColor(): string {
    return (
      this.effectiveBrand?.style.accentColor ??
      this.composition.branding.accentColor
    );
  }
  get logoAlternativeText(): string {
    return (
      this.assetCatalog?.items.find((item) =>
        sameAssetReference(item.reference, this.effectiveBrand?.style.logo),
      )?.alternativeText ?? ''
    );
  }
  get logoStyle(): Record<string, string> {
    const style = this.effectiveBrand?.style;
    return {
      width: `${style?.logoWidthPt ?? 54}pt`,
      height: `${style?.logoHeightPt ?? 54}pt`,
      objectFit: 'contain',
    };
  }
  get logoAlignment(): string {
    const alignment = this.effectiveBrand?.style.logoAlignment;
    return alignment === 'center'
      ? 'center'
      : alignment === 'end'
        ? 'flex-end'
        : 'flex-start';
  }
  get watermarkStyle(): Record<string, string | number> {
    const style = this.effectiveBrand?.style;
    if (!style) return {};
    const layout = noticeBrandWatermarkLayout(
      style.watermarkText,
      this.composition.page.size,
      style.watermarkRotation,
    );
    return {
      color: style.watermarkColor,
      opacity: Math.round(style.watermarkOpacity * 255) / 255,
      top: '50%',
      left: '50%',
      right: 'auto',
      transform: `translate(-50%, -50%) rotate(${style.watermarkRotation}deg)`,
      width: `${layout.widthPt}pt`,
      height: `${layout.heightPt}pt`,
      fontSize: `${layout.fontSizePt}pt`,
      whiteSpace: 'pre',
      lineHeight: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };
  }
  get watermarkText(): string {
    return this.effectiveBrand?.style.watermarkText ?? 'SYNTHETIC DRAFT';
  }
  get showLogo(): boolean {
    if (this.composition.branding.profile)
      return (
        !!this.effectiveBrand?.style.logo &&
        !!this.brandLogoUrl &&
        !this.brandError
      );
    return (
      this.composition.branding.logoCode === 'synthetic-seal-v1' &&
      noticeDocumentLogoChoices(this.catalog).some(
        (choice) => choice.code === 'synthetic-seal-v1',
      )
    );
  }
  get showWatermark(): boolean {
    if (this.composition.branding.profile)
      return !!this.effectiveBrand?.style.watermarkText && !this.brandError;
    return (
      this.composition.branding.watermarkCode === 'synthetic-draft-v1' &&
      noticeDocumentWatermarkChoices(this.catalog).some(
        (choice) => choice.code === 'synthetic-draft-v1',
      )
    );
  }
  get unavailableBranding(): boolean {
    if (this.composition.branding.profile)
      return !this.effectiveBrand || !!this.brandError;
    return (
      (this.composition.branding.logoCode !== 'none' && !this.showLogo) ||
      (this.composition.branding.watermarkCode !== 'none' &&
        !this.showWatermark)
    );
  }
  openDocumentSettings(
    property: 'headerText' | 'footerText' | 'size' = 'size',
    event?: Event,
  ): void {
    if (this.disabled) return;
    this.closeInsertionPicker(false);
    this.cancelIssueFocus();
    this.breakHistoryGroup.emit();
    this.settingsReturnTarget =
      event?.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : (this.pageSetupTrigger?.nativeElement ?? null);
    this.propertiesOpen = true;
    this.inspectorMode = 'document';
    this.referenceSelection = null;
    this.rememberViewState();
    const generation = ++this.panelFocusGeneration;
    const identity = this.viewIdentity;
    const active = this.workspace?.nativeElement.ownerDocument.activeElement;
    requestAnimationFrame(() => {
      if (
        !this.destroyed &&
        this.editable &&
        this.viewIdentity === identity &&
        this.panelFocusGeneration === generation &&
        this.inspectorMode === 'document' &&
        this.workspace?.nativeElement.ownerDocument.activeElement === active
      )
        this.documentSettings?.focus(property);
    });
  }
  toggleOutline(): void {
    this.closeInsertionPicker(false);
    this.cancelIssueFocus();
    this.outlineOpen = !this.outlineOpen;
    this.rememberViewState();
  }
  changeZoom(zoom: number): void {
    if (![0.5, 0.75, 1].includes(zoom)) return;
    this.closeInsertionPicker(false);
    this.cancelIssueFocus();
    this.zoom = zoom;
    this.rememberViewState();
  }
  showElementProperties(): void {
    this.closeInsertionPicker(false);
    this.cancelIssueFocus();
    this.inspectorMode = 'element';
    this.referenceSelection = null;
    this.rememberViewState();
  }
  toggleProperties(): void {
    this.closeInsertionPicker(false);
    this.cancelIssueFocus();
    this.propertiesOpen = !this.propertiesOpen;
    this.panelFocusGeneration++;
    this.settingsReturnTarget = this.propertiesTrigger?.nativeElement ?? null;
    this.rememberViewState();
  }
  closeProperties(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    this.cancelIssueFocus();
    event.preventDefault();
    event.stopPropagation();
    this.propertiesOpen = false;
    this.panelFocusGeneration++;
    const target = this.settingsReturnTarget;
    const returnTarget =
      target?.isConnected &&
      !this.propertiesPane?.nativeElement.contains(target)
        ? target
        : this.pageSetupTrigger?.nativeElement;
    returnTarget?.focus({ preventScroll: true });
    this.rememberViewState();
  }
  documentSetting(setting: NoticeDocumentSetting): void {
    if (!this.editable) return;
    const result = applyNoticeDocumentSetting(
      this.composition,
      this.catalog,
      setting,
      this.assetCatalog,
    );
    this.settingsError = result.ok
      ? ''
      : `This change was not applied. ${result.message}`;
    if (
      !result.ok ||
      JSON.stringify(result.composition) === JSON.stringify(this.composition)
    )
      return;
    this.publish(
      result.composition,
      this.selectedBlockId,
      setting.section === 'branding' &&
        (setting.property === 'headerText' || setting.property === 'footerText')
        ? `document:${setting.property}`
        : undefined,
    );
  }
  label(block: NoticeBlock): string {
    const index =
      this.composition.blocks.findIndex((b) => b.id === block.id) + 1;
    return `${block.kind === 'box' ? 'Box' : block.kind === 'repeat' ? 'Table' : block.kind === 'pageBreak' ? 'Page break' : 'Text'} block ${index}`;
  }
  columnWidth(block: NoticeBlock, width: number): number {
    return (
      (100 * width) /
      (block.columns?.reduce((sum, column) => sum + column.width, 0) || 1)
    );
  }
  blockStyle(block: NoticeBlock): Record<string, string | number> {
    const s = block.style!;
    return {
      fontSize: `${s.fontSizePt}pt`,
      color: s.color,
      backgroundColor: s.backgroundColor,
      border: `${s.borderWidthPt}pt solid ${s.borderColor}`,
      padding: `${s.paddingPt}pt`,
    };
  }
  select(block: NoticeBlock, explicit = true): void {
    if (this.disabled) return;
    if (explicit || this.selected?.id !== block.id)
      this.closeInsertionPicker(false);
    this.cancelIssueFocus();
    if (this.referenceSelection?.blockId !== block.id) {
      this.referenceSelection = null;
      // Tabbing into text updates selection without discarding Page setup.
      if (explicit || this.inspectorMode !== 'document')
        this.inspectorMode = 'element';
    }
    this.selectedBlockId = block.id;
    this.selectedBlockIdChange.emit(block.id);
    this.insertError.set('');
    this.rememberViewState();
  }
  references(block: NoticeBlock): NoticeReferenceSelection[] {
    return noticeReferenceSelections(block);
  }
  referenceLabel(selection: NoticeReferenceSelection): string {
    const entries = selection.kind === 'field' ? this.fields : this.terms;
    return (
      entries.find((item) => item.code === selection.code)?.label ??
      `Unknown: ${selection.code}`
    );
  }
  inspectReference(
    block: NoticeBlock,
    selection: Omit<NoticeReferenceSelection, 'blockId'> | null,
  ): void {
    if (!this.editable) return;
    this.cancelIssueFocus();
    if (!selection) {
      if (this.referenceSelection?.blockId === block.id) {
        this.referenceSelection = null;
        this.inspectorMode = 'element';
      }
      return;
    }
    const current = this.references(block).find(
      (item) =>
        item.referenceIndex === selection.referenceIndex &&
        item.kind === selection.kind &&
        item.code === selection.code &&
        item.format === selection.format,
    );
    if (!current) return;
    this.select(block);
    this.referenceSelection = current;
    this.referenceError = '';
    this.propertiesOpen = true;
    this.inspectorMode = 'reference';
    this.breakHistoryGroup.emit();
    this.rememberViewState();
  }
  referenceCommand(command: NoticeReferenceCommand): void {
    if (!this.editable || !this.referenceSelection) return;
    const result = applyNoticeReferenceCommand(
      this.composition,
      this.catalog,
      this.referenceSelection,
      command,
    );
    this.referenceError = result.ok
      ? ''
      : `This change was not applied. ${result.message}`;
    if (
      !result.ok ||
      JSON.stringify(result.composition) === JSON.stringify(this.composition)
    )
      return;
    if (this.publish(result.composition, result.selection.blockId))
      this.referenceSelection = result.selection;
  }
  get resolvedReferenceText(): string | null {
    const selection = this.referenceSelection;
    const proof = this.proof;
    if (!selection || !proof) return null;
    try {
      if (
        JSON.stringify(JSON.parse(proof.sourceJson)) !==
        JSON.stringify(this.composition)
      )
        return null;
      return (
        proof.referenceEvidence?.find(
          (item) =>
            item.blockId === selection.blockId &&
            item.referenceIndex === selection.referenceIndex &&
            item.kind === selection.kind &&
            item.code === selection.code &&
            item.format === selection.format,
        )?.text ?? null
      );
    } catch {
      return null;
    }
  }
  get referenceTermEvidence() {
    const selection = this.referenceSelection;
    return selection?.kind === 'term' && this.resolvedReferenceText !== null
      ? (this.proof?.contextEvidence?.termEvidence.find(
          (item) => item.code === selection.code,
        ) ?? null)
      : null;
  }
  get referenceTerminologyNode() {
    const term = this.referenceTermEvidence;
    return term
      ? (this.proof?.terminologySnapshot?.nodes.find(
          (node) =>
            node.configurationCode === term.configurationCode &&
            node.tokenCode === term.tokenCode,
        ) ?? null)
      : null;
  }
  get referenceProofLabel(): string {
    return this.resolvedReferenceText === null
      ? ''
      : `Captured PDF · ${this.proof!.exampleCode}${this.proof!.contextEvidence ? ' · ' + this.proof!.contextEvidence.timeZone : ' · fixed synthetic example'}`;
  }
  async returnToReference(): Promise<void> {
    const selection = this.referenceSelection;
    if (!selection || !this.editable) return;
    const index = this.composition.blocks
      .filter((block) => !!block.runs)
      .findIndex((block) => block.id === selection.blockId);
    await this.editors.get(index)?.focusReference({
      referenceIndex: selection.referenceIndex,
      kind: selection.kind,
      code: selection.code,
      ...(selection.format === undefined ? {} : { format: selection.format }),
    });
  }
  changeRuns(block: NoticeBlock, runs: NoticeRun[]): void {
    if (!this.editable) return;
    const next = structuredClone(this.composition);
    next.blocks.find((b) => b.id === block.id)!.runs = runs.length
      ? structuredClone(runs)
      : [{ kind: 'literal', value: '', bold: false, italic: false }];
    this.publish(next, block.id, `typing:${block.id}`);
  }
  style<K extends keyof NoticeStyle>(property: K, value: NoticeStyle[K]): void {
    const block = this.selected;
    if (!this.editable || !block?.style) return;
    if (typeof value === 'number' && !Number.isFinite(value)) return;
    const next = structuredClone(this.composition);
    next.blocks.find((b) => b.id === block.id)!.style![property] = value;
    this.publish(next, block.id);
  }
  move(direction: -1 | 1): void {
    const block = this.selected;
    if (block)
      this.structure({ type: 'move', blockId: block.id, direction }, false);
  }
  get canAddBlock(): boolean {
    return (
      this.editable &&
      this.composition.blocks.length < Math.min(16, this.catalog.limits.blocks)
    );
  }
  get canAddTable(): boolean {
    return (
      !this.contextEnabled ||
      !!this.catalog.documentContext?.lists.some((list) =>
        list.fields.some((field) => field.formats.length > 0),
      )
    );
  }
  outlineText(block: NoticeBlock): string {
    if (block.kind === 'pageBreak') return 'Start a new page';
    if (block.kind === 'repeat')
      return (
        this.catalog.documentContext?.lists.find(
          (list) => list.code === block.source,
        )?.label ?? 'Repeated items'
      );
    const text =
      block.runs
        ?.map((run) =>
          run.kind === 'literal'
            ? run.value
            : ((run.kind === 'field' ? this.fields : this.terms).find(
                (token) => token.code === run.value,
              )?.label ?? 'Unknown reference'),
        )
        .join('') ?? '';
    return text.replace(/\s+/g, ' ').trim().slice(0, 72) || 'Empty text';
  }
  selectOutline(block: NoticeBlock): void {
    this.select(block);
    if (!this.disabled) this.focusBlock(block.id);
  }
  insertBlock(): void {
    const anchor = this.selected;
    if (!anchor || !this.canAddBlock) return;
    if (
      this.structure(
        {
          type: 'insert',
          kind: this.insertionKind,
          anchorId: anchor.id,
          position: this.insertionPosition,
        },
        false,
      )
    ) {
      this.closeInsertionPicker(false);
      this.workspace?.nativeElement.focus({ preventScroll: true });
      this.focusBlock(this.selected!.id);
    }
  }
  duplicateBlock(): void {
    const block = this.selected;
    if (block) this.structure({ type: 'duplicate', blockId: block.id });
  }
  removeBlock(): void {
    const block = this.selected;
    if (block) this.structure({ type: 'remove', blockId: block.id });
  }
  structure(command: NoticeStructureCommand, focus = true): boolean {
    if (!this.editable) return false;
    const result = applyNoticeStructureCommand(
      this.composition,
      this.catalog,
      command,
    );
    this.structureError = !result.ok;
    if (!result.ok) {
      this.structureMessage = `This change was not applied. ${result.message}`;
      return false;
    }
    if (
      !this.publish(
        result.composition,
        result.selectedBlockId,
        command.type === 'column-edit' && command.property === 'label'
          ? `column-label:${command.blockId}:${command.columnId}`
          : undefined,
      )
    )
      return false;
    this.inspectorMode = 'element';
    this.referenceSelection = null;
    this.structureMessage =
      command.type.startsWith('column-') || command.type === 'list'
        ? 'Table updated. Undo restores the previous structure.'
        : `${this.label(this.selected!)} selected. Structure updated; Undo is available.`;
    if (this.selected?.kind === 'repeat') this.propertiesOpen = true;
    if (focus) this.focusBlock(result.selectedBlockId);
    return true;
  }
  private focusBlock(id: string): void {
    const scopeIdentity = this.scopeIdentity;
    const viewIdentity = this.viewIdentity;
    const source = JSON.stringify(this.composition);
    const active = this.host.nativeElement.ownerDocument.activeElement;
    requestAnimationFrame(() => {
      if (
        this.destroyed ||
        !this.editable ||
        this.scopeIdentity !== scopeIdentity ||
        this.viewIdentity !== viewIdentity ||
        JSON.stringify(this.composition) !== source ||
        this.host.nativeElement.ownerDocument.activeElement !== active ||
        this.selected?.id !== id
      )
        return;
      const block = Array.from(
        this.canvas?.nativeElement.querySelectorAll<HTMLElement>(
          '[data-block-id]',
        ) ?? [],
      ).find((item) => item.dataset['blockId'] === id);
      block?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      const target =
        block?.querySelector<HTMLElement>('[role="textbox"],button') ?? block;
      target?.focus();
    });
  }
  private focusRequestedBlock(): void {
    if (this.pendingIssueFocus) return;
    const id = this.pendingOpenFocus;
    if (!id || !this.editable) return;
    this.pendingOpenFocus = null;
    if (this.selected?.id === id) this.focusBlock(id);
  }

  private cancelIssueFocus(): void {
    this.pendingIssueFocus = null;
    this.scheduledIssueFocus = null;
  }

  private issueIdentityCurrent(request: PendingIssueFocus): boolean {
    return (
      !this.destroyed &&
      !this.disabled &&
      this.pendingIssueFocus === request &&
      this.viewIdentity === request.viewIdentity &&
      this.scopeIdentity === request.scopeIdentity &&
      JSON.stringify(this.composition) === request.source &&
      this.selectedBlockId === request.selectedBlockId &&
      this.panelFocusGeneration === request.generation &&
      this.host.nativeElement.ownerDocument.activeElement ===
        request.activeElement
    );
  }

  /** Focus changes presentation only; all repair edits still belong to the source owner. */
  private focusRequestedIssue(): void {
    const request = this.pendingIssueFocus;
    if (!request || this.scheduledIssueFocus === request || !this.editable)
      return;
    if (!this.issueIdentityCurrent(request)) {
      this.cancelIssueFocus();
      return;
    }
    const target = request.target;
    if (target.kind === 'fields') {
      this.unavailableIssueFocus();
      return;
    }
    this.settingsReturnTarget =
      request.activeElement instanceof HTMLElement &&
      request.activeElement.tagName !== 'BODY'
        ? request.activeElement
        : null;
    if (target.kind === 'document-setting') {
      this.propertiesOpen = true;
      this.inspectorMode = 'document';
      this.referenceSelection = null;
    } else {
      const blockId =
        target.kind === 'reference' ? target.selection.blockId : target.blockId;
      const block = this.composition.blocks.find((item) => item.id === blockId);
      if (!block || this.selected?.id !== block.id) {
        this.unavailableIssueFocus();
        return;
      }
      this.propertiesOpen = true;
      this.inspectorMode = 'element';
      this.referenceSelection = null;
      if (target.kind === 'reference') {
        const selection = target.selection;
        const reference = this.references(block).find(
          (item) =>
            item.referenceIndex === selection.referenceIndex &&
            item.kind === selection.kind &&
            item.code === selection.code &&
            item.format === selection.format,
        );
        if (!reference) {
          this.unavailableIssueFocus();
          return;
        }
        this.referenceSelection = reference;
        this.inspectorMode = 'reference';
      }
    }
    this.scheduledIssueFocus = request;
    requestAnimationFrame(() => {
      if (this.scheduledIssueFocus === request) this.scheduledIssueFocus = null;
      if (!this.editable || !this.issueIdentityCurrent(request)) return;
      this.pendingIssueFocus = null;
      const focused =
        target.kind === 'document-setting'
          ? this.documentSettings?.focus(target.property) === true
          : this.focusIssueControl(target);
      if (!focused) this.issueFocusUnavailable.emit();
      this.rememberViewState();
    });
  }

  private unavailableIssueFocus(): void {
    const request = this.pendingIssueFocus;
    if (!request || this.scheduledIssueFocus === request) return;
    this.scheduledIssueFocus = request;
    requestAnimationFrame(() => {
      if (this.scheduledIssueFocus === request) this.scheduledIssueFocus = null;
      if (!this.issueIdentityCurrent(request)) return;
      this.cancelIssueFocus();
      this.issueFocusUnavailable.emit();
    });
  }

  private focusIssueControl(
    target: Exclude<
      NoticeDiagnosticTarget,
      { kind: 'document-setting' | 'fields' }
    >,
  ): boolean {
    const pane = this.propertiesPane?.nativeElement;
    const named = (
      container: HTMLElement | undefined,
      name: string,
    ): HTMLElement | undefined =>
      Array.from(container?.querySelectorAll<HTMLElement>('[name]') ?? []).find(
        (control) => control.getAttribute('name') === name,
      );
    let control: HTMLElement | null | undefined;
    if (target.kind === 'reference') {
      control = named(pane, `reference-${target.property}`);
      if (!control && target.property === 'format')
        control = pane?.querySelector<HTMLElement>(
          '[data-unavailable="format"] button',
        );
    } else if (target.kind === 'table-column') {
      const column = Array.from(
        pane?.querySelectorAll<HTMLElement>('[data-column-id]') ?? [],
      ).find((element) => element.dataset['columnId'] === target.columnId);
      const index =
        this.selected?.columns?.findIndex(
          (item) => item.id === target.columnId,
        ) ?? -1;
      const labels = {
        label: 'Heading',
        field: 'Field',
        format: 'Format',
        width: 'Width',
      };
      // Dynamic NgModel names are directive inputs, not DOM attributes. Match
      // the real accessible control inside the verified column identity.
      if (index >= 0)
        control = Array.from(
          column?.querySelectorAll<HTMLElement>('input,select') ?? [],
        ).find(
          (element) =>
            element.getAttribute('aria-label') ===
            `${labels[target.property]} for column ${index + 1}`,
        );
    } else if (target.property === 'text') {
      const block = Array.from(
        this.canvas?.nativeElement.querySelectorAll<HTMLElement>(
          '[data-block-id]',
        ) ?? [],
      ).find((element) => element.dataset['blockId'] === target.blockId);
      control = block?.querySelector<HTMLElement>('[role="textbox"]');
    } else if (target.property === 'source') {
      control = named(pane, 'table-list');
    } else if (target.property === 'structure') {
      control = pane?.querySelector<HTMLElement>(
        '.block-actions button:not(:disabled)',
      );
    } else {
      const names: Record<keyof NoticeStyle, string> = {
        fontSizePt: 'page-text-size',
        color: 'page-text-color',
        backgroundColor: 'page-fill-color',
        borderColor: 'page-border-color',
        borderWidthPt: 'page-border-width',
        paddingPt: 'page-padding',
        keepTogether: 'page-keep-together',
      };
      control = named(pane, names[target.property]);
    }
    if (!control || control.matches(':disabled') || control.closest('[hidden]'))
      return false;
    control.focus({ preventScroll: true });
    if (control.ownerDocument.activeElement !== control) return false;
    control.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    return true;
  }
  private publish(
    next: NoticeComposition,
    selectedBlockId: string | null,
    groupKey?: string,
  ): boolean {
    this.cancelIssueFocus();
    const change: NoticePageChange = {
      composition: next,
      selectedBlockId,
      ...(groupKey === undefined ? {} : { groupKey }),
    };
    this.documentChange.emit(change);
    // Keep accepted rapid edits until Angular rebinds the parent source. Rejected
    // edits cannot create a separate local document while access/busy state changes.
    if (change.accepted) {
      this.composition = next;
      this.selectedBlockId = selectedBlockId;
      return true;
    }
    return false;
  }
  searchTokens(query: string): void {
    this.pendingTokenInsertion = null;
    this.tokenSearch = query;
    if (
      !this.filteredTokenOptions.some((token) => token.code === this.tokenCode)
    )
      this.chooseToken('');
  }
  chooseTokenKind(kind: 'field' | 'term'): void {
    if (kind !== 'field' && kind !== 'term') return;
    this.pendingTokenInsertion = null;
    this.tokenKind = kind;
    this.chooseToken(this.filteredTokenOptions[0]?.code ?? '');
  }
  chooseToken(code: string): void {
    this.pendingTokenInsertion = null;
    this.tokenCode = this.filteredTokenOptions.some(
      (token) => token.code === code,
    )
      ? code
      : '';
    this.tokenFormat = this.tokenCode ? (this.tokenFormats[0] ?? '') : '';
  }
  openTokenPicker(): void {
    if (!this.editable || !this.selected?.runs) return;
    this.openInsertionPicker('token');
  }
  openBlockPicker(): void {
    if (!this.canAddBlock || !this.selected) return;
    this.openInsertionPicker('block');
  }
  private openInsertionPicker(mode: 'token' | 'block'): void {
    if (this.insertionMode === mode) {
      this.closeInsertionPicker();
      return;
    }
    const activeControl = this.host.nativeElement.ownerDocument.activeElement;
    const replacingFocusedPanel =
      !!activeControl &&
      !!this.insertionPanelHost?.nativeElement.contains(activeControl);
    this.closeInsertionPicker(false);
    if (replacingFocusedPanel) {
      const trigger = mode === 'token' ? this.tokenTrigger : this.blockTrigger;
      trigger?.nativeElement.focus({ preventScroll: true });
    }
    this.cancelIssueFocus();
    this.tokenPickerOpen = mode === 'token';
    this.blockPickerOpen = mode === 'block';
    if (mode === 'token') this.chooseTokenKind(this.tokenKind);
    const generation = ++this.panelFocusGeneration;
    const identity = this.viewIdentity;
    const scope = this.scopeIdentity;
    const source = this.composition;
    const signature = JSON.stringify(source);
    const selected = this.selected?.id;
    const active = this.host.nativeElement.ownerDocument.activeElement;
    requestAnimationFrame(() => {
      if (
        !this.destroyed &&
        this.editable &&
        this.insertionMode === mode &&
        this.viewIdentity === identity &&
        this.scopeIdentity === scope &&
        this.composition === source &&
        JSON.stringify(this.composition) === signature &&
        this.selected?.id === selected &&
        this.panelFocusGeneration === generation &&
        this.host.nativeElement.ownerDocument.activeElement === active
      )
        this.insertionPanel?.focus();
    });
  }
  closeInsertionPicker(returnFocus = true): void {
    const mode = this.insertionMode;
    if (
      !mode &&
      !this.pendingTokenInsertion &&
      !this.tokenSearch &&
      !this.tokenCode &&
      !this.tokenFormat
    )
      return;
    this.tokenPickerOpen = false;
    this.blockPickerOpen = false;
    this.tokenSearch = '';
    this.tokenCode = '';
    this.tokenFormat = '';
    this.pendingTokenInsertion = null;
    this.panelFocusGeneration++;
    if (returnFocus && mode && !this.destroyed && this.editable) {
      const trigger = mode === 'token' ? this.tokenTrigger : this.blockTrigger;
      trigger?.nativeElement.focus({ preventScroll: true });
    }
  }
  closeTokenPicker(event?: KeyboardEvent): void {
    if (event && (event.key !== 'Escape' || event.isComposing)) return;
    event?.preventDefault();
    event?.stopPropagation();
    this.closeInsertionPicker();
  }
  dismissInsertionOutside(event: PointerEvent): void {
    const target = event.target;
    if (
      this.insertionMode &&
      target instanceof Node &&
      !this.insertionPanelHost?.nativeElement.contains(target) &&
      !this.tokenTrigger?.nativeElement.contains(target) &&
      !this.blockTrigger?.nativeElement.contains(target)
    )
      this.closeInsertionPicker(false);
  }
  async insertToken(): Promise<void> {
    const selected = this.selected;
    if (
      !this.editable ||
      !this.tokenPickerOpen ||
      this.pendingTokenInsertion ||
      (this.tokenKind !== 'field' && this.tokenKind !== 'term') ||
      !selected?.runs ||
      !this.filteredTokenOptions.some(
        (token) => token.code === this.tokenCode,
      ) ||
      (this.tokenFormats.length
        ? !this.tokenFormats.includes(this.tokenFormat)
        : this.tokenFormat !== '')
    )
      return;
    const source = this.composition;
    const signature = JSON.stringify(source);
    const catalog = JSON.stringify(this.catalog);
    const scope = this.scopeIdentity;
    const identity = this.viewIdentity;
    const kind = this.tokenKind;
    const code = this.tokenCode;
    const format = this.tokenFormat;
    const search = this.tokenSearch;
    const active = this.host.nativeElement.ownerDocument.activeElement;
    const request = {};
    this.pendingTokenInsertion = request;
    const sameSession = (): boolean =>
      !this.destroyed &&
      this.editable &&
      this.viewIdentity === identity &&
      this.scopeIdentity === scope &&
      this.selected?.id === selected.id;
    const canInsert = (): boolean =>
      sameSession() &&
      this.pendingTokenInsertion === request &&
      this.tokenPickerOpen &&
      this.composition === source &&
      JSON.stringify(this.composition) === signature &&
      JSON.stringify(this.catalog) === catalog &&
      this.tokenKind === kind &&
      this.tokenCode === code &&
      this.tokenFormat === format &&
      this.tokenSearch === search &&
      this.filteredTokenOptions.some((token) => token.code === code) &&
      this.host.nativeElement.ownerDocument.activeElement === active;
    const index = this.composition.blocks
      .filter((block) => !!block.runs)
      .findIndex((block) => block.id === selected.id);
    this.breakHistoryGroup.emit();
    let inserted = false;
    try {
      inserted =
        (await this.editors
          .get(index)
          ?.insertToken(kind, code, format || undefined, canInsert)) ?? false;
    } catch {
      // An unavailable editing surface must not lose the retained composition.
    }
    if (inserted && sameSession()) this.breakHistoryGroup.emit();
    if (this.pendingTokenInsertion !== request) return;
    const stillCurrent = canInsert();
    this.pendingTokenInsertion = null;
    if (!inserted) {
      if (stillCurrent)
        this.insertError.set(
          'Select editable notice text and try inserting the token again.',
        );
      return;
    }
    if (sameSession()) this.closeInsertionPicker(false);
  }
  ngAfterViewInit(): void {
    this.restoreViewPosition();
    this.focusRequestedIssue();
  }
  rememberViewState(): void {
    if (this.destroyed) return;
    const state = this.captureViewState();
    this.lastKnownViewState = state;
    this.viewStateChange.emit({ identity: this.viewIdentity, state });
  }
  private captureViewState(): NoticePageViewState {
    const suspended = this.disabled ? this.suspendedViewState : null;
    const reference = suspended?.reference ?? this.referenceSelection;
    // A pending font/layout restore and a hidden/detached destruction pass
    // expose temporary zero offsets. Keep the last live positions instead.
    const canvas = this.canvas?.nativeElement;
    const retained =
      this.pendingViewRestore ??
      (!canvas?.isConnected || canvas.closest('[hidden]')
        ? this.lastKnownViewState
        : null);
    return {
      zoom: this.zoom,
      outlineOpen: this.outlineOpen,
      propertiesOpen: this.propertiesOpen,
      inspectorMode: suspended?.inspectorMode ?? this.inspectorMode,
      ...(reference
        ? {
            reference: {
              blockId: reference.blockId,
              referenceIndex: reference.referenceIndex,
            },
          }
        : {}),
      canvasScrollTop: retained?.canvasScrollTop ?? canvas?.scrollTop ?? 0,
      canvasScrollLeft: retained?.canvasScrollLeft ?? canvas?.scrollLeft ?? 0,
      propertiesScrollTop:
        retained?.propertiesScrollTop ??
        this.propertiesPane?.nativeElement.scrollTop ??
        0,
      outlineScrollTop:
        retained?.outlineScrollTop ??
        this.outlinePane?.nativeElement.scrollTop ??
        0,
      workspaceScrollTop:
        retained?.workspaceScrollTop ??
        this.workspace?.nativeElement.scrollTop ??
        0,
    };
  }
  private resetView(): void {
    this.closeInsertionPicker(false);
    this.cancelIssueFocus();
    this.viewGeneration++;
    this.panelFocusGeneration++;
    this.pendingViewRestore = null;
    this.viewRestoreScheduled = false;
    this.lastKnownViewState = null;
    this.suspendedViewState = null;
    this.zoom = 0.75;
    this.outlineOpen = false;
    this.propertiesOpen = true;
    this.inspectorMode = 'element';
    this.referenceSelection = null;
    this.settingsReturnTarget = null;
    this.tokenPickerOpen = false;
    this.blockPickerOpen = false;
    for (const pane of [
      this.canvas,
      this.propertiesPane,
      this.outlinePane,
      this.workspace,
    ]) {
      if (!pane) continue;
      pane.nativeElement.scrollTop = 0;
      pane.nativeElement.scrollLeft = 0;
    }
  }
  private acceptViewState(state: NoticePageViewState): void {
    this.lastKnownViewState = state;
    this.zoom = [0.5, 0.75, 1].includes(state.zoom) ? state.zoom : 0.75;
    this.outlineOpen = !!state.outlineOpen;
    this.propertiesOpen = !!state.propertiesOpen;
    this.inspectorMode =
      state.inspectorMode === 'document' ? 'document' : 'element';
    this.referenceSelection = null;
    if (state.inspectorMode === 'reference' && state.reference) {
      const reference = state.reference;
      const block = this.composition.blocks.find(
        (item) =>
          item.id === reference.blockId && item.id === this.selected?.id,
      );
      this.referenceSelection = block
        ? (this.references(block).find(
            (item) => item.referenceIndex === reference.referenceIndex,
          ) ?? null)
        : null;
      if (this.referenceSelection) this.inspectorMode = 'reference';
    }
    this.pendingViewRestore = state;
    this.restoreViewPosition();
  }
  private restoreViewPosition(): void {
    if (
      !this.pendingViewRestore ||
      this.viewRestoreScheduled ||
      !this.fontReady() ||
      this.disabled ||
      this.destroyed
    )
      return;
    const state = this.pendingViewRestore;
    const generation = this.viewGeneration;
    this.viewRestoreScheduled = true;
    requestAnimationFrame(() => {
      if (generation !== this.viewGeneration) return;
      this.viewRestoreScheduled = false;
      if (this.destroyed || this.disabled || !this.fontReady()) return;
      if (this.pendingViewRestore !== state) {
        this.restoreViewPosition();
        return;
      }
      this.pendingViewRestore = null;
      const position = (value: number): number =>
        Number.isFinite(value) ? Math.max(0, value) : 0;
      if (this.canvas) {
        this.canvas.nativeElement.scrollTop = position(state.canvasScrollTop);
        this.canvas.nativeElement.scrollLeft = position(state.canvasScrollLeft);
      }
      if (this.propertiesPane)
        this.propertiesPane.nativeElement.scrollTop = position(
          state.propertiesScrollTop,
        );
      if (this.outlinePane)
        this.outlinePane.nativeElement.scrollTop = position(
          state.outlineScrollTop,
        );
      if (this.workspace)
        this.workspace.nativeElement.scrollTop = position(
          state.workspaceScrollTop,
        );
    });
  }
  ngOnChanges(changes: SimpleChanges = {}): void {
    if (
      (changes['composition'] && !changes['composition'].firstChange) ||
      (changes['selectedBlockId'] && !changes['selectedBlockId'].firstChange) ||
      (changes['catalog'] && !changes['catalog'].firstChange)
    )
      this.closeInsertionPicker(false);
    const replacement = changes['viewIdentity'];
    if (
      replacement ||
      (changes['scopeIdentity'] && !changes['scopeIdentity'].firstChange)
    )
      this.resetView();
    if (
      changes['viewState'] &&
      this.viewState &&
      this.viewState !== this.lastKnownViewState
    )
      this.acceptViewState(this.viewState);
    if (changes['scopeIdentity']) this.pendingOpenFocus = null;
    if (
      changes['composition'] ||
      changes['selectedBlockId'] ||
      changes['scopeIdentity'] ||
      changes['disabled']
    )
      this.cancelIssueFocus();
    if (changes['focusBlockOnOpen'])
      this.pendingOpenFocus = this.focusBlockOnOpen;
    if (
      changes['focusIssueOnOpen'] &&
      this.focusIssueOnOpen &&
      !this.disabled
    ) {
      this.pendingOpenFocus = null;
      this.viewGeneration++;
      this.pendingViewRestore = null;
      this.viewRestoreScheduled = false;
      this.pendingIssueFocus = {
        target: this.focusIssueOnOpen,
        source: JSON.stringify(this.composition),
        viewIdentity: this.viewIdentity,
        scopeIdentity: this.scopeIdentity,
        selectedBlockId: this.selectedBlockId,
        activeElement: this.host.nativeElement.ownerDocument.activeElement,
        generation: ++this.panelFocusGeneration,
      };
    } else if (changes['focusIssueOnOpen']) this.cancelIssueFocus();
    if (
      changes['selectedBlockId'] &&
      this.pendingOpenFocus &&
      this.selectedBlockId !== this.pendingOpenFocus
    )
      this.pendingOpenFocus = null;
    if (changes['scopeIdentity'] && !changes['scopeIdentity'].firstChange) {
      this.referenceSelection = null;
      this.referenceError = '';
      if (this.inspectorMode === 'reference') this.inspectorMode = 'element';
    }
    if (
      this.referenceSelection &&
      (changes['composition'] || changes['selectedBlockId'])
    ) {
      const selection = this.referenceSelection;
      const block = this.composition.blocks.find(
        (item) => item.id === selection.blockId,
      );
      if (
        !block ||
        this.selected?.id !== block.id ||
        !this.references(block).some(
          (item) =>
            item.referenceIndex === selection.referenceIndex &&
            item.kind === selection.kind &&
            item.code === selection.code &&
            item.format === selection.format,
        )
      ) {
        this.referenceSelection = null;
        if (this.inspectorMode === 'reference') this.inspectorMode = 'element';
      }
    }
    if (changes['disabled'] && !this.disabled && this.suspendedViewState) {
      this.acceptViewState(this.suspendedViewState);
      this.suspendedViewState = null;
    }
    if (this.disabled) {
      this.closeInsertionPicker(false);
      // A save/render temporarily disables editing. Preserve its view while
      // clearing active reference controls; a replaced identity discards it.
      if (changes['disabled'] && this.referenceSelection) {
        this.suspendedViewState = this.captureViewState();
      }
      this.panelFocusGeneration++;
      this.pendingOpenFocus = null;
      this.tokenPickerOpen = false;
      this.blockPickerOpen = false;
      this.structureMessage = '';
      this.settingsError = '';
      this.referenceSelection = null;
      this.referenceError = '';
      if (this.inspectorMode === 'reference') this.inspectorMode = 'element';
      this.insertError.set('');
    }
    const descriptors = this.catalog.fontFiles;
    const identity = JSON.stringify(descriptors ?? []);
    if (identity === this.fontIdentity) {
      this.restoreViewPosition();
      if (this.fontError() && this.pendingIssueFocus)
        this.unavailableIssueFocus();
      else this.focusRequestedIssue();
      this.focusRequestedBlock();
      return;
    }
    this.fontIdentity = identity;
    this.closeInsertionPicker(false);
    this.fontReady.set(false);
    this.fontError.set('');
    const generation = ++this.fontGeneration;
    if (!descriptors?.length) {
      this.fontError.set(
        'The pinned page font is unavailable. Use Fields while the renderer is refreshed.',
      );
      if (this.pendingIssueFocus) this.unavailableIssueFocus();
      return;
    }
    void this.fonts
      .load(descriptors)
      .then(() => {
        if (!this.destroyed && generation === this.fontGeneration) {
          this.fontReady.set(true);
          this.restoreViewPosition();
          this.focusRequestedIssue();
          this.focusRequestedBlock();
        }
      })
      .catch((error: unknown) => {
        if (this.destroyed || generation !== this.fontGeneration) return;
        this.fontIdentity = '';
        if (error instanceof NoticeFontAccessError) {
          this.accessLost.emit();
          return;
        }
        this.fontError.set(
          'The page font could not be verified. Your source is preserved; use Fields or refresh access to retry.',
        );
        if (this.pendingIssueFocus) this.unavailableIssueFocus();
      });
  }
  ngOnDestroy(): void {
    this.closeInsertionPicker(false);
    this.cancelIssueFocus();
    this.rememberViewState();
    this.destroyed = true;
    this.viewGeneration++;
    this.panelFocusGeneration++;
    this.pendingOpenFocus = null;
    this.fontGeneration++;
  }
}
